// SB6-ANA-HEADER-02 — Asset Info widget burger menu.
//
// Christian's 2026-06-05 review: the topmost analysis widget (asset info +
// pickers) had accumulated too many icon buttons, and the lock Release even
// consumed a whole canvas row. This menu consolidates the lower-frequency
// actions behind ONE overflow (MoreVert) burger in the widget's top-right
// corner. The header keeps only the information-bearing + high-frequency
// controls visible (asset info, pickers, prescreening indicator, undo, redo,
// and this burger).
//
// Menu order (locked by Christian), with dividers between functional groups:
//   1. Reload all          (was `analysis-header-reload-all`)
//   2. Release             (NEW — self-unassign; see below)
//   3. Reject              (moved from WorkflowHeaderActions, Story 027)
//   4. Send back to the pilot…  (DI-3-R1-FE-analyst, 2026-10-01 — Christian:
//                          "in this menu we already have the reject button")
//   ──────────────────────  dataset group
//   5. Download dataset    (was `download-dataset`)
//   6. Pin dataset         (was `pin-dataset-toggle`; reflects pinned state)
//   ──────────────────────  view group
//   7. Split view          (was SplitViewButton, Story 022)
//   ──────────────────────
//   8. Settings            (always last)
//
// Release = self-unassign (Decisions §2). It clears MY phase-relevant
// assignee slot via `updateInspectionAssignee(userId: null)`, releasing the
// edit lock first when held. The BE self-unassign role-gate (R58-W-09) is
// SLOT-AWARE in a way that only covers the EXPERT slot through this surface:
// the controller's role-gate reads the DA's `expert_user_id` as the "current
// assignee", so a non-admin pre_screener self-unassigning in PRE_SCREENING is
// rejected (403). Per the story contingency we therefore SHIP Release for the
// expert phase only and HIDE it in PRE_SCREENING; the prescreener-slot gap is
// filed in docs/cross-repo-outbox (FEAT-… header-02).
import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { DataAcquisitionState } from "@aeriosiq/types";
import { useWorkspaceUiStore } from "../state/workspaceUiStore.js";
import { useSelectionStore } from "../state/selectionStore.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useSplitViewStore } from "../split-view/splitViewStore.js";
import { usePrefetchControls } from "../cache/PrefetchProvider.js";
import { useCacheManifestEntry } from "../cache/useCacheManifest.js";
import { setPinned } from "../cache/persistence.js";
import {
  SEND_BACK_NOTE_MAX,
  SEND_BACK_NOTE_MIN,
  SEND_BACK_SUCCESS_TEXT,
  sendBackRefusalText,
  useRejectDataAcquisitionMutation,
  useSendBackToPilotMutation,
} from "../workflowMutations.js";
import {
  SENT_BACK_NOTICE_KEY,
  type SentBackNoticeState,
} from "../../data-acquisitions/SentBackNotice.js";
import { useSelfUnassignMutation } from "../queries.js";
import {
  BanIcon,
  CheckIcon,
  CircleAlertIcon,
  Columns3Icon,
  DownloadIcon,
  EllipsisVerticalIcon,
  InboxIcon,
  LogOutIcon,
  PinIcon,
  RefreshCwIcon,
  SettingsIcon,
  XIcon,
} from "@aeriosiq/ui/icons";

// ANA-R1-12 — static tooltip copy for the fixed-wording menu items. Kept as a
// flat map (one en string per item) so the structure is ready for a future
// `de` lookup (AC2). The state-dependent items (Download / Pin / Split view)
// compute their wording inline next to their dynamic labels.
const TOOLTIP_COPY = {
  // Dispels the "this resets everything" fear ([00:30:04]): Reload only
  // re-fetches the current data; it touches no settings and discards no work.
  reloadAll:
    "Re-fetches the latest data for what you are viewing. It does not reset your settings or discard any of your work.",
  release:
    "Removes you as the assignee so another analyst can take this over. Your edit lock is released; your reviewed work is kept.",
  reject:
    "Sends this data acquisition back for revision, with a reason and the phase to return it to.",
  sendBack:
    "Returns this data set to the pilot's intake for rework, with a note telling them what to fix. Your findings stay on their images.",
  settings:
    "Opens analysis settings, including the layout and widgets options.",
} as const;

// DI-3-R1 4.2 (b) — the analyst roles intake/v1 `reopen` accepts with a note
// (`REOPEN_ANALYST_ROLES`, BE `own-tenant-gate.ts`). A pilot is never here.
const SEND_BACK_ROLES = [
  "pre_screener",
  "expert_analyst",
  "project_manager",
  "tenant_admin",
  "platform_admin",
] as const;

export interface AssetInfoBurgerMenuProps {
  dataAcquisitionId: string;
  evaluationState: DataAcquisitionState | null;
  prescreenerUserId: string | null;
  expertUserId: string | null;
  meId: string | null | undefined;
  meRoles: readonly string[] | undefined;
}

type Snack =
  | { kind: "success"; text: string }
  | { kind: "error"; text: string }
  | null;

export function AssetInfoBurgerMenu({
  dataAcquisitionId,
  evaluationState,
  prescreenerUserId,
  expertUserId,
  meId,
  meRoles,
}: AssetInfoBurgerMenuProps): React.ReactElement {
  const qc = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = anchorEl !== null;
  const closeMenu = (): void => {
    setAnchorEl(null);
  };

  // --- Reject (Story 027 — moved here verbatim) ----------------------------
  const isPm =
    (meRoles?.includes("project_manager") ?? false) ||
    (meRoles?.includes("tenant_admin") ?? false);
  const showReject =
    isPm &&
    (evaluationState === "PRE_SCREENING" ||
      evaluationState === "EXPERT_ANALYSIS" ||
      evaluationState === "EVALUATION_DONE");

  // --- Send back to the pilot (DI-3-R1-FE-analyst) -------------------------
  // intake/v1 `reopen` for an analyst role (DI-3-R1 4.2 b): the roles the
  // route accepts with a note, and ONLY in the route's window (PROCESSING /
  // PRE_SCREENING / EXPERT_ANALYSIS). Outside it the item is absent, not
  // disabled (EVALUATION_DONE and later → the route answers 409).
  const isSendBackRole = SEND_BACK_ROLES.some(
    (r) => meRoles?.includes(r) ?? false,
  );
  const showSendBack =
    isSendBackRole &&
    (evaluationState === "PROCESSING" ||
      evaluationState === "PRE_SCREENING" ||
      evaluationState === "EXPERT_ANALYSIS");

  // --- Release (NEW — self-unassign, expert-phase only per contingency) ----
  // The endpoint's self-unassign gate only covers the EXPERT slot through
  // this surface (see file header), so Release is hidden in PRE_SCREENING.
  const showRelease =
    evaluationState === "EXPERT_ANALYSIS" && !!meId && expertUserId === meId;
  // Keep the prescreener identity referenced so the visibility rule reads as
  // a deliberate per-phase decision rather than a dropped branch.
  void prescreenerUserId;

  // --- Dataset cache controls ----------------------------------------------
  const prefetch = usePrefetchControls();
  const cacheEntry = useCacheManifestEntry(dataAcquisitionId);
  const pinned = cacheEntry?.pinned ?? false;

  // --- Split view ----------------------------------------------------------
  const splitActive = useSplitViewStore((s) => s.active);
  const openSplit = useSplitViewStore((s) => s.open);
  const closeSplit = useSplitViewStore((s) => s.close);
  const activeFindingId = useSelectionStore((s) => s.activeFindingId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const splitCanOpen = Boolean(activeFindingId && activeImageId);

  // --- Settings + reset + lock release (lifted into the UI store) ----------
  const openSettingsTab = useWorkspaceUiStore((s) => s.openSettingsTab);
  const releaseLock = useWorkspaceUiStore((s) => s.releaseLock);

  // --- Dialog + snackbar state ---------------------------------------------
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectTargetPhase, setRejectTargetPhase] = useState<
    "PRE_SCREENING" | "EXPERT_ANALYSIS"
  >("EXPERT_ANALYSIS");
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState("");
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [meteredOpen, setMeteredOpen] = useState(false);
  const [snack, setSnack] = useState<Snack>(null);

  const rejectMutation = useRejectDataAcquisitionMutation();
  const sendBackMutation = useSendBackToPilotMutation();
  const navigate = useNavigate();
  const selfUnassignMutation = useSelfUnassignMutation(dataAcquisitionId);

  // --- Handlers ------------------------------------------------------------
  const onReloadAll = (): void => {
    closeMenu();
    void qc.invalidateQueries();
  };

  const onReleaseConfirm = (): void => {
    setReleaseOpen(false);
    // Drop the edit lock first when we hold it (idempotent no-op otherwise),
    // then clear the assignee slot.
    releaseLock?.();
    selfUnassignMutation.mutate(undefined, {
      onSuccess: () => {
        setSnack({
          kind: "success",
          text: "You have been released from this data acquisition",
        });
      },
      onError: () => {
        setSnack({
          kind: "error",
          text: "Could not release you — please retry",
        });
      },
    });
  };

  const onRejectConfirm = (): void => {
    if (rejectReason.length < 3) return;
    setRejectOpen(false);
    rejectMutation.mutate(
      {
        dataAcquisitionId,
        reason: rejectReason,
        targetPhase: rejectTargetPhase,
      },
      {
        onSuccess: () => {
          setSnack({ kind: "success", text: "Data acquisition rejected" });
          setRejectReason("");
        },
        onError: () => {
          setSnack({ kind: "error", text: "Could not reject — please retry" });
        },
      },
    );
  };

  const sendBackNoteTrimmed = sendBackNote.trim();
  const sendBackNoteValid =
    sendBackNoteTrimmed.length >= SEND_BACK_NOTE_MIN &&
    sendBackNoteTrimmed.length <= SEND_BACK_NOTE_MAX;

  const onSendBackConfirm = (): void => {
    if (!sendBackNoteValid) return;
    setSendBackOpen(false);
    sendBackMutation.mutate(
      {
        dataAcquisitionId,
        note: sendBackNoteTrimmed,
        fromState: evaluationState,
      },
      {
        onSuccess: () => {
          setSendBackNote("");
          // The set has left analysis (CREATED, back in the pilot's
          // intake): leave its page for the list, carrying the sentence.
          const state: SentBackNoticeState = {
            [SENT_BACK_NOTICE_KEY]: SEND_BACK_SUCCESS_TEXT,
          };
          void navigate("/data-acquisitions", { state });
        },
        onError: (err: unknown) => {
          setSnack({ kind: "error", text: sendBackRefusalText(err) });
        },
      },
    );
  };

  const onDownloadDataset = (): void => {
    closeMenu();
    if (!prefetch) return;
    if (prefetch.progress.isDownloading) {
      prefetch.cancelDataset();
      return;
    }
    if (prefetch.saveDataHint) {
      setMeteredOpen(true);
      return;
    }
    prefetch.downloadDataset();
  };

  const onPinToggle = (): void => {
    closeMenu();
    void setPinned(dataAcquisitionId, !pinned);
  };

  const onSplitView = (): void => {
    closeMenu();
    if (splitActive) {
      closeSplit();
      return;
    }
    if (!splitCanOpen || !activeImageId || !activeFindingId) return;
    openSplit({
      mode: "link-defects",
      primaryImageId: activeImageId,
      primaryDefectId: activeFindingId,
    });
  };

  const onSettings = (): void => {
    closeMenu();
    // The burger's Settings always opens the Layout & Widgets context first
    // (it's where "Reset analysis widgets to default" now lives).
    openSettingsTab("layout");
  };

  // Download label/icon reflect the live prefetch state, mirroring the old
  // DownloadDatasetButton's three faces (idle / downloading / error).
  const downloadLabel = !prefetch
    ? "Download dataset"
    : prefetch.progress.isDownloading
      ? "Cancel download"
      : prefetch.progress.error
        ? "Retry download"
        : "Download dataset";

  // ANA-R1-12 — burger-menu hover tooltips. Every item gets one plain-language
  // sentence describing exactly what pressing it does, derived from the
  // handler's actual behaviour above (not the label). The "Reload all" copy
  // explicitly dispels the "this resets everything" fear Christian raised
  // ([00:30:04]) — it only re-fetches the current data. Dynamic items
  // (Download / Pin / Split view) carry copy that matches their current
  // action face. Plain en strings, inline like the menu labels (HEADER-02 did
  // not i18n the labels); the flat `TOOLTIP_COPY` map keeps the structure
  // ready for a future `de` lookup (AC2). The two state-dependent items below
  // pick their wording the same way the labels do.
  const downloadTooltip = !prefetch
    ? "Downloading is not available yet for this data set."
    : prefetch.progress.isDownloading
      ? "Stops the download that is currently running."
      : prefetch.progress.error
        ? "Tries the download again after the last attempt failed."
        : "Downloads all images of this data set to your device now.";
  const pinTooltip = pinned
    ? "Stops keeping this data set on your device; its cached images may be cleared later."
    : "Keeps this data set's images on your device so they stay available offline and open instantly.";
  const splitTooltip = splitActive
    ? "Closes the second viewer pane and returns to the single image view."
    : "Opens a second viewer pane beside the current one so you can compare two images side by side.";

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          // AC5 — XS-scale overflow button consistent with the compact
          // presets. `size="small"` + the 24px square keeps it at the same
          // scale as the undo/redo cluster it sits beside.
          size="small"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
          }}
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? "asset-info-burger-menu" : undefined}
          data-testid="asset-info-burger"
          sx={{ width: 24, height: 24 }}
        >
          <EllipsisVerticalIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        id="asset-info-burger-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ "aria-label": "Asset actions" }}
      >
        {/* 1 — Reload all */}
        <Tooltip title={TOOLTIP_COPY.reloadAll} placement="left">
          <MenuItem
            onClick={onReloadAll}
            data-testid="analysis-header-reload-all"
          >
            <ListItemIcon>
              <RefreshCwIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reload all</ListItemText>
          </MenuItem>
        </Tooltip>

        {/* 2 — Release (self-unassign). Conditional render (expert phase,
            slot-holder only) — matches the old lock-release's conditional. */}
        {showRelease ? (
          <Tooltip title={TOOLTIP_COPY.release} placement="left">
            <MenuItem
              onClick={() => {
                closeMenu();
                setReleaseOpen(true);
              }}
              data-testid="release-self-unassign"
            >
              <ListItemIcon>
                <LogOutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Release</ListItemText>
            </MenuItem>
          </Tooltip>
        ) : null}

        {/* 3 — Reject (Story 027). Conditional on PM/admin + phase. */}
        {showReject ? (
          <Tooltip title={TOOLTIP_COPY.reject} placement="left">
            <MenuItem
              onClick={() => {
                closeMenu();
                setRejectOpen(true);
              }}
              data-testid="workflow-reject"
            >
              <ListItemIcon>
                <BanIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText sx={{ color: "error.main" }}>Reject</ListItemText>
            </MenuItem>
          </Tooltip>
        ) : null}

        {/* 4 — Send back to the pilot… (DI-3-R1-FE-analyst). Conditional on
            an analyst role + the reopen window (absent, never disabled). */}
        {showSendBack ? (
          <Tooltip title={TOOLTIP_COPY.sendBack} placement="left">
            <MenuItem
              onClick={() => {
                closeMenu();
                setSendBackOpen(true);
              }}
              data-testid="send-back-to-pilot"
            >
              <ListItemIcon>
                <InboxIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Send back to the pilot…</ListItemText>
            </MenuItem>
          </Tooltip>
        ) : null}

        <Divider />

        {/* 5 — Download dataset. The item can be disabled (no prefetch
            controls yet); MUI needs a <span> wrapper so the tooltip still
            surfaces on hover over the disabled item. */}
        <Tooltip title={downloadTooltip} placement="left">
          <span>
            <MenuItem
              onClick={onDownloadDataset}
              disabled={!prefetch}
              data-testid="download-dataset"
            >
              <ListItemIcon>
                {prefetch?.progress.isDownloading ? (
                  <XIcon fontSize="small" />
                ) : prefetch?.progress.error ? (
                  <CircleAlertIcon fontSize="small" color="error" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText>{downloadLabel}</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>

        {/* 6 — Pin dataset (a toggle → menuitemcheckbox so `aria-checked`
            reflects the pinned state; also a trailing check icon). */}
        <Tooltip title={pinTooltip} placement="left">
          <MenuItem
            onClick={onPinToggle}
            data-testid="pin-dataset-toggle"
            role="menuitemcheckbox"
            aria-checked={pinned}
          >
            <ListItemIcon>
              {/* Filled when pinned. Material carried this as PushPin vs
                  PushPinOutlined; Lucide has one pin, so the pair survives as
                  fill-vs-stroke rather than as two drawings. */}
              <PinIcon
                fontSize="small"
                filled={pinned}
                {...(pinned ? { color: "primary" as const } : {})}
              />
            </ListItemIcon>
            <ListItemText>
              {pinned ? "Unpin dataset" : "Pin dataset"}
            </ListItemText>
            {pinned ? <CheckIcon fontSize="small" color="primary" /> : null}
          </MenuItem>
        </Tooltip>

        <Divider />

        {/* 7 — Split view (an on/off toggle → menuitemcheckbox so the
            active state is conveyed via `aria-checked`). */}
        <Tooltip title={splitTooltip} placement="left">
          {/* <span> wrapper so the tooltip surfaces while the item is disabled
              (no active defect + image yet). */}
          <span>
            <MenuItem
              onClick={onSplitView}
              disabled={!splitActive && !splitCanOpen}
              data-testid="split-view-menu-item"
              role="menuitemcheckbox"
              aria-checked={splitActive}
            >
              <ListItemIcon>
                <Columns3Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {splitActive ? "Exit split view" : "Split view"}
              </ListItemText>
            </MenuItem>
          </span>
        </Tooltip>

        <Divider />

        {/* 8 — Settings (always last) */}
        <Tooltip title={TOOLTIP_COPY.settings} placement="left">
          <MenuItem
            onClick={onSettings}
            data-testid="workspace-settings-menu-item"
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
        </Tooltip>
      </Menu>

      {/* Release (self-unassign) confirm dialog. */}
      <Dialog
        open={releaseOpen}
        onClose={() => {
          setReleaseOpen(false);
        }}
        aria-labelledby="release-self-title"
      >
        <DialogTitle id="release-self-title">
          Release me from this data acquisition?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This clears your assignment so another analyst can pick it up. Your
            edit lock is released as part of this; your reviewed work is kept.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReleaseOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onReleaseConfirm}
            data-testid="release-confirm"
          >
            Release me
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog (Story 027 — moved here unchanged). */}
      <Dialog
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
        }}
        aria-labelledby="reject-title"
      >
        <DialogTitle id="reject-title">Reject data acquisition?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ minWidth: 380, mt: 0.5 }}>
            <Typography>
              Rejecting sends the data acquisition back for revision. Provide a
              reason and the target phase.
            </Typography>
            <TextField
              label="Reason for rejection"
              value={rejectReason}
              onChange={(e) => {
                setRejectReason(e.target.value);
              }}
              multiline
              minRows={2}
              required
              slotProps={{
                htmlInput: { "aria-label": "Reason for rejection" },
              }}
              helperText={`${String(rejectReason.length)} / 3 chars minimum`}
            />
            <FormControl>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Send back to:
              </Typography>
              <RadioGroup
                row
                value={rejectTargetPhase}
                onChange={(e) => {
                  setRejectTargetPhase(
                    e.target.value as "PRE_SCREENING" | "EXPERT_ANALYSIS",
                  );
                }}
              >
                <FormControlLabel
                  value="PRE_SCREENING"
                  control={<Radio />}
                  label="Pre-Screening"
                />
                <FormControlLabel
                  value="EXPERT_ANALYSIS"
                  control={<Radio />}
                  label="Expert Analysis"
                />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onRejectConfirm}
            disabled={rejectReason.length < 3}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send back to the pilot (DI-3-R1-FE-analyst) — Reject's dialog
          pattern; the note is required (3–500 chars after trimming). */}
      <Dialog
        open={sendBackOpen}
        onClose={() => {
          setSendBackOpen(false);
        }}
        aria-labelledby="send-back-title"
      >
        <DialogTitle id="send-back-title">Send back to the pilot?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ minWidth: 380, mt: 0.5 }}>
            <Typography>
              The data set returns to the pilot&apos;s intake for rework. Your
              findings stay on their images. Tell the pilot what to fix:
            </Typography>
            <TextField
              label="Note for the pilot"
              value={sendBackNote}
              onChange={(e) => {
                setSendBackNote(e.target.value);
              }}
              multiline
              minRows={2}
              required
              slotProps={{
                htmlInput: {
                  "aria-label": "Note for the pilot",
                  maxLength: SEND_BACK_NOTE_MAX,
                },
              }}
              helperText={`${String(sendBackNoteTrimmed.length)} / ${String(SEND_BACK_NOTE_MAX)} · ${String(SEND_BACK_NOTE_MIN)} chars minimum`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSendBackOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSendBackConfirm}
            disabled={!sendBackNoteValid || sendBackMutation.isPending}
            data-testid="send-back-confirm"
          >
            Send back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Metered-download confirm (moved from DownloadDatasetButton). */}
      <Dialog
        open={meteredOpen}
        onClose={() => {
          setMeteredOpen(false);
        }}
        aria-labelledby="metered-download-title"
      >
        <DialogTitle id="metered-download-title">
          Download on a metered connection?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You appear to be on a metered or data-saver connection. Downloading
            the whole dataset can transfer a few gigabytes of full-resolution
            images. You can continue, or wait for a faster connection.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMeteredOpen(false);
            }}
            data-testid="metered-cancel"
          >
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setMeteredOpen(false);
              prefetch?.downloadDataset();
            }}
            data-testid="metered-confirm"
          >
            Download anyway
          </Button>
        </DialogActions>
      </Dialog>

      {snack ? (
        <Snackbar
          open
          autoHideDuration={snack.kind === "error" ? 6000 : 3000}
          onClose={() => {
            setSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snack.kind}
            variant="filled"
            onClose={() => {
              setSnack(null);
            }}
          >
            {snack.text}
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
}
